import { ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useStoreActions } from '~/store/hooks';
import { useApiRequest } from '~/hooks/api';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import Button from '~/ui/components/common/Button';
import InfoMessage from '~/ui/components/common/InfoMessage';
import Loader from '~/ui/components/common/Loader';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import AdmissionDetails from './components/AdmissionDetails';
import FormActions from './components/FormActions';
import Location from './components/Location';
import PersonalDetails from './components/PersonalDetails';
import { FORM_LOAD_ERROR } from './constants';
import { IInitialValues } from './initialValues';
import validate, { editValidationSchema } from './validate';

interface IProps {
  isProcessing: boolean;
  leavePageDescription: string;
  clinicId: string;
  submitText?: string;
  defaultValues?: IInitialValues;
  onSubmit: (val: IInitialValues) => void;
}

const Form = ({
  isProcessing,
  leavePageDescription,
  clinicId,
  submitText = 'Add Client',
  defaultValues = { aot: false } as IInitialValues,
  onSubmit,
}: IProps): ReactElement => {
  const onGetAllActTeamsAvailable = useStoreActions(
    actions => actions.actTeam.onGetAllActTeamsAvailable,
  );
  const onGetAvailableShelters = useStoreActions(
    actions => actions.shelters.onGetAvailableShelters,
  );
  const onGetDictionariesByType = useStoreActions(
    actions => actions.dictionaries.onGetDictionariesByType,
  );

  const { isLoading, data, refetch } = useApiRequest(
    [
      () => onGetAllActTeamsAvailable({ clinicId }),
      () => onGetAvailableShelters({ clinicId }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.Gender }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.Race }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.ReferralSource }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.State }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.GenderIdentity }),
      () => onGetDictionariesByType({ type: IDictionaryTypes.EducationLevel }),
    ],
    { initialLoading: true, dependencies: [clinicId] },
  );

  const isEditing = !!defaultValues.firstName;

  const methods = useForm<IInitialValues>({
    defaultValues,
    mode: 'onBlur',
    resolver: isEditing ? editValidationSchema : validate,
  });
  const {
    formState: { isDirty },
  } = methods;

  if (isLoading) return <Loader />;

  if (!data) {
    return (
      <>
        <InfoMessage message={FORM_LOAD_ERROR} />
        <Button variant="outlined" color="primary" onClick={refetch}>
          Try again
        </Button>
      </>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PersonalDetails isEditing={isEditing} />
        {!isEditing && <Location />}
        <AdmissionDetails />
        <FormActions isProcessing={isProcessing} submitText={submitText} />
      </form>
      <NavigationConfirmModal when={isDirty && !isProcessing} description={leavePageDescription} />
    </FormProvider>
  );
};

export default Form;
